import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    CircularProgress,
    IconButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    Grid
} from '@mui/material';
import { Security, Save as SaveIcon, ExpandMore, ExpandLess, Refresh, SelectAll } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { blobticMessage } from '../../components/BlobticMessage';
import { useLanguage } from '../../contexts/LanguageContext';
import { coreFeatureService } from '../../services/coreFeatureService';
import GenericPage from '../../components/GenericPage';
import { ZoomField } from '../../components/ZoomField';
import { ZoomData } from '../../services/blobticZoomService';

// Interfaces
interface Permission {
    LOAD: boolean;
    SELECT: boolean;
    UPDATE: boolean;
    DELETE: boolean;
    INSERT: boolean;
    EXECUTE: boolean;
}

interface SecurityObject {
    SYSOBJ_IDF: number;
    SYSOBJ_CODE: string;
    SYSOBJ_LIB: string;
    SYSOBJ_TYPE: 'object' | 'action';
    SYSOBJ_WEB_FORM?: string;
    PSYSOBJ_IDF?: number;
    // Rights
    PRFOBJ_IDF?: number; // If null, no rights yet
    PRFOBJ_LOAD?: number;
    PRFOBJ_SEL?: number;
    PRFOBJ_UPDT?: number;
    PRFOBJ_DEL?: number;
    PRFOBJ_INSERT?: number;
    PRFOBJ_EXE?: number;
    // Metadata
    LAST_UPDT?: string;
    USR_LIB?: string;
    // Frontend helper
    children?: SecurityObject[];
    permissions?: Permission;
}

const SecurityWebPage: React.FC = () => {
    
    const { t } = useLanguage();



    // State
    const [selectedProfile, setSelectedProfile] = useState<ZoomData | null>(null);
    const [objects, setObjects] = useState<SecurityObject[]>([]);
    const [loading, setLoading] = useState(false);
    const [savingId, setSavingId] = useState<number | null>(null);
    // Track expanded parents
    const [expandedIds, setExpandedIds] = useState<number[]>([]);

    // Bulk Update State
    const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false);
    const [isBulkUpdating, setIsBulkUpdating] = useState(false);
    const [bulkPermissions, setBulkPermissions] = useState<Permission>({
        LOAD: false,
        SELECT: false,
        UPDATE: false,
        DELETE: false,
        INSERT: false,
        EXECUTE: false
    });

    // Load Objects
    const loadObjects = async (profileId: number) => {
        setLoading(true);
        try {
            const result = await coreFeatureService.callServerAction(
                'Blobtic_Sys_Profil,sys_security_web,select',
                {
                    SELECT_ALL_OBJECTS_WITH_RIGHTS: true,
                    PRF_IDF: profileId
                }
            );

            let rawList: any[] = [];
            if (result.success) {
                if (Array.isArray(result.data)) rawList = result.data;
                else if (result.data && typeof result.data === 'object') rawList = [result.data];
            } else {
                throw new Error(result.message || 'Unknown error');
            }

            // Process Data: Convert to Tree
            const parents: SecurityObject[] = [];
            const childrenMap: { [key: number]: SecurityObject[] } = {};

            rawList.forEach((item: any) => {
                const obj: SecurityObject = {
                    ...item,
                    permissions: {
                        LOAD: item.PRFOBJ_LOAD === 1,
                        SELECT: item.PRFOBJ_SEL === 1,
                        UPDATE: item.PRFOBJ_UPDT === 1,
                        DELETE: item.PRFOBJ_DEL === 1,
                        INSERT: item.PRFOBJ_INSERT === 1,
                        EXECUTE: item.PRFOBJ_EXE === 1
                    }
                };

                if (obj.SYSOBJ_TYPE === 'object') {
                    obj.children = [];
                    parents.push(obj);
                } else {
                    if (obj.PSYSOBJ_IDF) {
                        if (!childrenMap[obj.PSYSOBJ_IDF]) childrenMap[obj.PSYSOBJ_IDF] = [];
                        childrenMap[obj.PSYSOBJ_IDF].push(obj);
                    }
                }
            });

            // Link Children
            parents.forEach(p => {
                if (childrenMap[p.SYSOBJ_IDF]) {
                    p.children = childrenMap[p.SYSOBJ_IDF];
                }
            });

            setObjects(parents);

        } catch (err: any) {
            console.error(err);
            blobticMessage.error(t('common.error.loading'), err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedProfile) {
            loadObjects(selectedProfile.PRF_IDF);
        } else {
            setObjects([]);
        }
    }, [selectedProfile]);

    const handleProfileChange = (val: string, data?: ZoomData) => {
        if (data) setSelectedProfile(data);
        else if (!val) setSelectedProfile(null);
    };

    const handleToggleRight = (objId: number, right: keyof Permission) => {
        const updateList = (list: SecurityObject[]): SecurityObject[] => {
            return list.map(item => {
                if (item.SYSOBJ_IDF === objId) {
                    const newPerms = { ...item.permissions!, [right]: !item.permissions![right] };
                    return { ...item, permissions: newPerms };
                }
                if (item.children) {
                    return { ...item, children: updateList(item.children) };
                }
                return item;
            });
        };
        setObjects(updateList(objects));
    };

    const handleSaveRow = async (item: SecurityObject) => {
        if (!selectedProfile) return;
        setSavingId(item.SYSOBJ_IDF);

        try {
            const result = await coreFeatureService.callServerAction(
                'Blobtic_Sys_Profil,sys_security_web,update',
                {
                    UPDATE_RIGHTS: true,
                    PRF_IDF: selectedProfile.PRF_IDF,
                    SYSOBJ_IDF: item.SYSOBJ_IDF,
                    PRFOBJ_LOAD: item.permissions?.LOAD ? 1 : 0,
                    PRFOBJ_SEL: item.permissions?.SELECT ? 1 : 0,
                    PRFOBJ_UPDT: item.permissions?.UPDATE ? 1 : 0,
                    PRFOBJ_DEL: item.permissions?.DELETE ? 1 : 0,
                    PRFOBJ_INSERT: item.permissions?.INSERT ? 1 : 0,
                    PRFOBJ_EXE: item.permissions?.EXECUTE ? 1 : 0
                }
            );

            if (!result.success) throw new Error(result.message);

            // Update local state with returned data
            if (result.data && Array.isArray(result.data) && result.data.length > 0) {
                const updatedRaw = result.data[0];

                // Convert raw to internal format
                const updatedObj: SecurityObject = {
                    ...updatedRaw,
                    permissions: {
                        LOAD: updatedRaw.PRFOBJ_LOAD === 1,
                        SELECT: updatedRaw.PRFOBJ_SEL === 1,
                        UPDATE: updatedRaw.PRFOBJ_UPDT === 1,
                        DELETE: updatedRaw.PRFOBJ_DEL === 1,
                        INSERT: updatedRaw.PRFOBJ_INSERT === 1,
                        EXECUTE: updatedRaw.PRFOBJ_EXE === 1
                    }
                };

                // Recursive update of the list
                const updateState = (list: SecurityObject[]): SecurityObject[] => {
                    return list.map(obj => {
                        if (obj.SYSOBJ_IDF === updatedObj.SYSOBJ_IDF) {
                            // Merge updated data (keeping children structure if any)
                            return { ...obj, ...updatedObj, children: obj.children };
                        }
                        if (obj.children) {
                            return { ...obj, children: updateState(obj.children) };
                        }
                        return obj;
                    });
                };

                setObjects((prev: SecurityObject[]) => updateState(prev));
            }

            blobticMessage.success(t('common.messages.success'), t('common.messages.saved'));

        } catch (err: any) {
            console.error(err);
            blobticMessage.error(t('common.error.saving'), err.message);
        } finally {
            setSavingId(null);
        }
    };

    const toggleExpand = (id: number) => {
        setExpandedIds((prev: number[]) =>
            prev.includes(id) ? prev.filter((p: number) => p !== id) : [...prev, id]
        );
    };

    // --- Bulk Updates Actions ---

    const handleRefresh = () => {
        if (selectedProfile) {
            loadObjects(selectedProfile.PRF_IDF);
        }
    };

    const handleOpenBulkDialog = () => {
        if (!selectedProfile) {
            blobticMessage.warning(t('common.messages.warning'), t('page.menuConfig.structure.empty'));
            return;
        }
        // Reset permissions for bulk diag
        setBulkPermissions({
            LOAD: false,
            SELECT: false,
            UPDATE: false,
            DELETE: false,
            INSERT: false,
            EXECUTE: false
        });
        setIsBulkDialogOpen(true);
    };

    const handleBulkCheckChange = (key: keyof Permission) => {
        setBulkPermissions((prev: Permission) => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const handleBulkApply = async () => {
        if (!selectedProfile) return;
        setIsBulkUpdating(true);
        try {
            const result = await coreFeatureService.callServerAction(
                'Blobtic_Sys_Profil,sys_security_web,update',
                {
                    UPDATE_ALL_RIGHTS: true,
                    PRF_IDF: selectedProfile.PRF_IDF,
                    PRFOBJ_LOAD: bulkPermissions.LOAD ? 1 : 0,
                    PRFOBJ_SEL: bulkPermissions.SELECT ? 1 : 0,
                    PRFOBJ_UPDT: bulkPermissions.UPDATE ? 1 : 0,
                    PRFOBJ_DEL: bulkPermissions.DELETE ? 1 : 0,
                    PRFOBJ_INSERT: bulkPermissions.INSERT ? 1 : 0,
                    PRFOBJ_EXE: bulkPermissions.EXECUTE ? 1 : 0
                }
            );

            if (!result.success) throw new Error(result.message);

            blobticMessage.success(t('common.messages.success'), t('common.messages.saved'));
            setIsBulkDialogOpen(false);
            // Reload grid
            loadObjects(selectedProfile.PRF_IDF);

        } catch (err: any) {
            console.error(err);
            blobticMessage.error(t('common.error.saving'), err.message);
        } finally {
            setIsBulkUpdating(false);
        }
    };

    // Render tree as flat list with indentation
    const renderRows = () => {
        if (loading && objects.length === 0) return null; // handled by parent spinner
        if (objects.length === 0) {
            return (
                <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                        {selectedProfile ? t('page.security.table.empty') : t('page.security.table.selectProfile')}
                    </TableCell>
                </TableRow>
            );
        }

        const rows: React.ReactNode[] = [];

        objects.forEach((obj: SecurityObject) => {
            const isExpanded = expandedIds.includes(obj.SYSOBJ_IDF);
            const hasChildren = obj.children && obj.children.length > 0;

            // Parent Row
            rows.push(
                <SecurityRow
                    key={obj.SYSOBJ_IDF}
                    item={obj}
                    level={0}
                    isExpanded={isExpanded}
                    hasChildren={!!hasChildren}
                    onToggleExpand={() => toggleExpand(obj.SYSOBJ_IDF)}
                    onToggleRight={handleToggleRight}
                    onSave={handleSaveRow}
                    savingId={savingId}
                />
            );

            // Children Rows
            if (isExpanded && hasChildren) {
                obj.children!.forEach((child: SecurityObject) => {
                    rows.push(
                        <SecurityRow
                            key={child.SYSOBJ_IDF}
                            item={child}
                            level={1}
                            isExpanded={false}
                            hasChildren={false}
                            onToggleExpand={() => { }}
                            onToggleRight={handleToggleRight}
                            onSave={handleSaveRow}
                            savingId={savingId}
                        />
                    );
                });
            }
        });
        return rows;
    };


    const headerActions = (
        <>
            <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={handleRefresh}
                disabled={!selectedProfile || loading}
            >
                {t('page.security.action.refresh')}
            </Button>
            <Button
                variant="contained"
                startIcon={<SelectAll />}
                onClick={handleOpenBulkDialog}
                disabled={!selectedProfile || loading}
            >
                {t('page.security.action.updateAll')}
            </Button>
        </>
    );

    return (
        <>
            <GenericPage
                title={t('page.security.title')}
                icon={<Security />}
                additionalActions={headerActions}
            >
                <Box sx={{ mb: 3, maxWidth: 400 }}>
                    <ZoomField
                        zoomKey="profil"
                        label={t('page.security.profile.label')}
                        placeholder={t('page.security.table.selectProfile')}
                        value={selectedProfile ? selectedProfile.PRF_CODE : ''}
                        displayField="PRF_CODE"
                        valueField="PRF_IDF"
                        onChange={handleProfileChange}
                        showSelectedInfo={true}
                        helperText={selectedProfile ? selectedProfile.PRF_LIB : undefined}
                    />
                </Box>

                <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                    {loading && objects.length === 0 ? (
                        <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
                    ) : (
                        <TableContainer sx={{ maxHeight: 'calc(100vh - 350px)' }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.default' }}>{t('page.security.table.objectAction')}</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'background.default', width: 60 }}>Load</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'background.default', width: 60 }}>Sel</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'background.default', width: 60 }}>Upd</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'background.default', width: 60 }}>Del</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'background.default', width: 60 }}>Ins</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'background.default', width: 60 }}>Exe</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.default', width: 150 }}>{t('common.labels.user')}</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.default', width: 150 }}>{t('common.labels.lastUpdate')}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'background.default', width: 80 }}>{t('common.actions.save')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {renderRows()}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>
            </GenericPage>



            {/* Bulk Update Dialog */}
            <Dialog open={isBulkDialogOpen} onClose={() => setIsBulkDialogOpen(false)}>
                <DialogTitle>{t('page.security.dialog.bulkTitle')}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        {['LOAD', 'SELECT', 'UPDATE', 'DELETE', 'INSERT', 'EXECUTE'].map((right) => (
                            <Grid item xs={4} key={right}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={bulkPermissions[right as keyof Permission]}
                                            onChange={() => handleBulkCheckChange(right as keyof Permission)}
                                        />
                                    }
                                    label={right}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsBulkDialogOpen(false)}>{t('btn.cancel')}</Button>
                    <Button
                        onClick={handleBulkApply}
                        variant="contained"
                        disabled={isBulkUpdating}
                        startIcon={isBulkUpdating ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {t('page.security.dialog.apply')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

// Sub-component for Row
interface SecurityRowProps {
    item: SecurityObject;
    level: number;
    isExpanded: boolean;
    hasChildren: boolean;
    onToggleExpand: () => void;
    onToggleRight: (id: number, right: keyof Permission) => void;
    onSave: (item: SecurityObject) => void;
    savingId: number | null;
}

const SecurityRow: React.FC<SecurityRowProps> = ({
    item, level, isExpanded, hasChildren, onToggleExpand, onToggleRight, onSave, savingId
}) => {
    return (
        <TableRow hover sx={{ bgcolor: level > 0 ? 'action.hover' : 'inherit' }}>
            <TableCell sx={{ pl: level === 0 ? 2 : 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {hasChildren ? (
                        <IconButton size="small" onClick={onToggleExpand} sx={{ mr: 1, p: 0.5 }}>
                            {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                        </IconButton>
                    ) : (
                        <Box sx={{ width: 28, mr: 1 }} />
                    )}
                    <Box>
                        <Typography variant="body2" fontWeight={level === 0 ? "bold" : "regular"}>{item.SYSOBJ_LIB || item.SYSOBJ_CODE}</Typography>
                        {item.SYSOBJ_LIB !== item.SYSOBJ_CODE && (
                            <Typography variant="caption" color="text.secondary" display="block">{item.SYSOBJ_CODE}</Typography>
                        )}
                    </Box>
                </Box>
            </TableCell>

            {['LOAD', 'SELECT', 'UPDATE', 'DELETE', 'INSERT', 'EXECUTE'].map((right) => (
                <TableCell key={right} align="center" padding="checkbox">
                    <Checkbox
                        size="small"
                        checked={!!item.permissions?.[right as keyof Permission]}
                        onChange={() => onToggleRight(item.SYSOBJ_IDF, right as keyof Permission)}
                    />
                </TableCell>
            ))}

            <TableCell>
                <Typography variant="body2" fontSize="small">{item.USR_LIB || '-'}</Typography>
            </TableCell>
            <TableCell>
                <Typography variant="body2" fontSize="small">{item.LAST_UPDT ? new Date(item.LAST_UPDT).toLocaleString() : '-'}</Typography>
            </TableCell>

            <TableCell align="right">
                <IconButton
                    onClick={() => onSave(item)}
                    disabled={savingId === item.SYSOBJ_IDF}
                    color="primary"
                    size="small"
                >
                    {savingId === item.SYSOBJ_IDF ? <CircularProgress size={20} /> : <SaveIcon fontSize="small" />}
                </IconButton>
            </TableCell>
        </TableRow>
    );
};

export default SecurityWebPage;
